import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { AdSpaceInterface, AdSpaceMediaTypeInterface, SizeDataTypeInterface, CurrencyInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpacesColumns } from './AdSpacesColumns';
import { AdSpacesUpdateDialog } from './AdSpacesUpdateDialog';

const AdSpacesAdminList: React.FC = () => {
    const [adSpaces, setAdSpaces] = useState<AdSpaceInterface[]>([]);
    const [adSpace, setAdSpace] = useState<AdSpaceInterface | null>(null);
    const [userId, setUserId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [adSpaceId, setAdSpaceId] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [adSpaceToDelete, setAdSpaceToDelete] = useState<AdSpaceInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [adSpaceToUpdate, setAdSpaceToUpdate] = useState<AdSpaceInterface | null>(null);
    const [updateForm, setUpdateForm] = useState<Partial<AdSpaceInterface>>({});
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [mediaTypes, setMediaTypes] = useState<AdSpaceMediaTypeInterface[]>([]);
    const [sizeDataTypes, setSizeDataTypes] = useState<SizeDataTypeInterface[]>([]);
    const [currencies, setCurrencies] = useState<CurrencyInterface[]>([]);


    // Fetch all ad spaces
    const handleGetAllAdSpaces = async () => {
        setError(null);
        setAdSpace(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<AdSpaceInterface[]>>('/adSpaces');
            if (result.status && result.data) {
                console.log(`Fetched adSpaces: ${JSON.stringify(result.data)}`);
                setAdSpaces(result.data);
            } else {
                setError(result.message || 'Failed to fetch ad spaces');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch ad spaces');
        }
    };

    // Fetch ad space by id
    const handleGetAdSpaceById = async () => {
        setError(null);
        setAdSpaces([]);
        setSuccess(null);
        if (!adSpaceId) return setError('Please enter an AdSpace ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpaceInterface>>(`/adSpaces/${adSpaceId}`);
            if (result.status && result.data) {
                setAdSpace(result.data);
            } else {
                setError(result.message || 'Failed to fetch ad space');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch ad space');
        }
    };

    // Fetch ad spaces by user id
    const handleGetAdSpacesByUserId = async () => {
        setError(null);
        setAdSpace(null);
        setSuccess(null);
        if (!userId) return setError('Please enter a User ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpaceInterface[]>>(`/adSpaces/userId/${userId}`);
            if (result.status && result.data) {
                setAdSpaces(result.data);
            } else {
                setError(result.message || 'Failed to fetch ad spaces for user');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch ad spaces for user');
        }
    };

    // Delete ad space
    const handleDeleteAdSpace = async () => {
        if (!adSpaceToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/adSpaces/delete/${adSpaceToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Ad space deleted successfully');
                setDeleteDialogOpen(false);
                setAdSpaceToDelete(null);
                handleGetAllAdSpaces();
            } else {
                setError(result.message || 'Failed to delete ad space');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete ad space');
        }
    };

    // Update ad space
const handleUpdateAdSpace = async () => {
    if (!adSpaceToUpdate || !adSpaceToUpdate.id) return;
    setUpdateError(null);

    // Prepare the correct GeoJSON object
    let longLatCoords = updateForm.longLatCoords ?? adSpaceToUpdate.longLatCoords;
    if (
        longLatCoords &&
        Array.isArray(longLatCoords.coordinates) &&
        longLatCoords.coordinates.length === 2
    ) {
        // user input must be lng, lat
        const [lat, lng] = longLatCoords.coordinates;
        longLatCoords = {
            type: 'Point',
            coordinates: [lng, lat],
        };
    }

    try {
        const result = await apiRequest<GenericResponseInterface<AdSpaceInterface>>(
            `/adSpaces/update/${adSpaceToUpdate.id}`,
            'PUT',
            {
                ...adSpaceToUpdate,
                ...updateForm,
                longLatCoords, // always send the correct structure
            }
        );
        if (result.status) {
            setSuccess(result.message || 'Ad space updated successfully');
            setUpdateDialogOpen(false);
            setAdSpaceToUpdate(null);
            setUpdateForm({});
            handleGetAllAdSpaces();
        } else {
            setUpdateError(result.message || 'Failed to update ad space');
        }
    } catch (err: any) {
        setUpdateError(err.message || 'Failed to update ad space');
    }
};

    const columns = getAdSpacesColumns(
        setAdSpaceToDelete,
        setDeleteDialogOpen,
        setAdSpaceToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllAdSpaces();
        apiGet<GenericResponseInterface<AdSpaceMediaTypeInterface[]>>('/adSpaceMediaTypes').then(result => {
            if (result.status && result.data) {
                setMediaTypes(result.data);
            }
        });
        apiGet<GenericResponseInterface<SizeDataTypeInterface[]>>('/sizeDataTypes').then(result => {
            if (result.status && result.data) {
                setSizeDataTypes(result.data);
            }
        });
        apiGet<GenericResponseInterface<CurrencyInterface[]>>('/currencies').then(result => {
            if (result.status && result.data) {
                setCurrencies(result.data);
            }
        });        
    }, []);

    useEffect(() => {
        if (updateDialogOpen && adSpaceToUpdate) {
            setUpdateForm({
                ...adSpaceToUpdate
            });
        }
    }, [updateDialogOpen, adSpaceToUpdate]);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllAdSpaces}>Get All Ad Spaces</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAdSpaceById}>Get Ad Space by ID</Button>
                <TextField
                    label="AdSpace ID"
                    value={adSpaceId}
                    onChange={e => setAdSpaceId(e.target.value)}
                    size="small"
                />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAdSpacesByUserId}>Get Ad Spaces by User ID</Button>
                <TextField
                    label="User ID"
                    value={userId}
                    onChange={e => setUserId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {adSpaces.length > 0 && (
                    <DataGrid
                        rows={adSpaces.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {adSpace && (
                    <DataGrid
                        rows={[{ ...adSpace, id: adSpace.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this ad space?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteAdSpace}>Delete</Button>
                </DialogActions>
            </Dialog>
            <AdSpacesUpdateDialog
                open={updateDialogOpen}
                adSpaceToUpdate={adSpaceToUpdate}
                setAdSpaceToUpdate={setAdSpaceToUpdate}
                updateForm={updateForm}
                setUpdateForm={setUpdateForm}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateAdSpace}
                updateError={updateError}
                updateSuccess={success}
                mediaTypes={mediaTypes}
                sizeDataTypes={sizeDataTypes}
                currencies={currencies}
            />
        </Box>
    );
};

export default AdSpacesAdminList;